import BadgeMask from '-/Badge/BadgeMask';
import Avatar from '-/Custom/Avatar';
import { DateTime } from 'luxon';
import { StyleSheet } from 'react-native-unistyles';
import type { TableNextColumn } from '@/components/Custom/TableNext.types';
import { TABLE_NEXT_LEADING_VISUAL_AUTOSIZE_EXTRA_WIDTH } from '@/components/Custom/TableNext.utils';
import {
  TableSelectionCheckbox,
  TableSelectionHeaderCheckbox,
} from '@/components/Custom/TableSelectionCheckbox';
import Box from '@/components/UI/Box';
import Checkbox from '@/components/UI/Checkbox';
import Text from '@/components/UI/Text';
import type { BadgeAssigneeBoolExp } from '@/gql/scalars';
import type { SelectionStore } from '@/hooks/useSelection';

const ORGANIZATION_ASSIGNMENT_SEARCH_MINIMUM = 1;
const NO_ORGANIZATION_ASSIGNMENT_ID = '00000000-0000-0000-0000-000000000000';
const SELECTION_COLUMN_WIDTH = 44;
const DYNAMIC_COLUMN_MAX_WIDTH = 288;
const ATTRIBUTE_COLUMN_MAX_WIDTH = 260;
const DATE_COLUMN_WIDTH = 184;
const ASSIGNMENT_TABLE_DATE_TIME_FORMAT = 'M/d/yyyy h:mma';

export type OrganizationAssignmentSearchTarget = 'badge' | 'person';

export type OrganizationAssignmentAttributeValue = {
  badge_attribute_id: string;
  formatted_value?: string | null;
  value?: string | null;
  badge_attribute: {
    title?: string | null;
  };
};

export type OrganizationAssignment = {
  badge: {
    color?: string | null;
    id: string;
    title?: string | null;
  };
  badge_assignee_attributes: OrganizationAssignmentAttributeValue[];
  created_at?: string | null;
  event?: {
    title?: string | null;
  } | null;
  event_id?: string | null;
  id: string;
  person?: {
    avatar_id?: string | null;
    color?: string | null;
    id: string;
    name?: string | null;
  } | null;
  person_id?: string | null;
  team?: {
    title?: string | null;
  } | null;
  team_id?: string | null;
  updated_at?: string | null;
};

type OrganizationAssignmentRowBase = {
  assignmentId: string;
  attributes: string;
  badgeColor: string | null;
  badgeId: string;
  badgeTitle: string;
  context: string;
  createdAt: string | null;
  eventId: string | null;
  id: string;
  personAvatarId: string | null;
  personColor: string | null;
  personId: string;
  personName: string;
  teamId: string | null;
  updatedAt: string | null;
};

export type OrganizationAssignmentRow = OrganizationAssignmentRowBase &
  Record<string, number | string | null>;

export type OrganizationAssignmentColumnAttribute = {
  id: string;
  title?: string | null;
};

type OrganizationAssignmentLegacySelectionOptions = {
  allVisibleAssignmentsSelected: boolean;
  onValueChangeAllVisibleAssignmentsSelected: (selected: boolean) => void;
  selectedAssignmentIds: string[];
  selectionStore?: undefined;
  toggleSelectedAssignment: (assignmentId: string, selected: boolean) => void;
  visibleAssignmentIds?: undefined;
};

type OrganizationAssignmentStoreSelectionOptions = {
  allVisibleAssignmentsSelected?: undefined;
  onValueChangeAllVisibleAssignmentsSelected?: undefined;
  selectedAssignmentIds?: undefined;
  selectionStore: SelectionStore<string>;
  toggleSelectedAssignment?: undefined;
  visibleAssignmentIds: string[];
};

type OrganizationAssignmentColumnsOptions = {
  assignmentCount: number;
  canSelectAssignments: boolean;
  selectedBadgeAttributes: OrganizationAssignmentColumnAttribute[];
  testIDPrefix: string;
} & (
  | OrganizationAssignmentLegacySelectionOptions
  | OrganizationAssignmentStoreSelectionOptions
);

type OrganizationAssignmentSelectionColumnsOptions = {
  assignmentCount: number;
  testIDPrefix: string;
} & (
  | OrganizationAssignmentLegacySelectionOptions
  | OrganizationAssignmentStoreSelectionOptions
);

export function getOrganizationMemberAssignmentColumns(
  options: OrganizationAssignmentColumnsOptions,
): TableNextColumn<OrganizationAssignmentRow>[] {
  return getOrganizationAssignmentColumns({
    ...options,
    primaryColumn: getBadgeColumn(),
  });
}

export function getOrganizationBadgeAssignmentColumns(
  options: OrganizationAssignmentColumnsOptions,
): TableNextColumn<OrganizationAssignmentRow>[] {
  return getOrganizationAssignmentColumns({
    ...options,
    primaryColumn: getPersonColumn(),
  });
}

export function getOrganizationAssignmentAttributeColumnId(
  attributeId: string,
) {
  return `attribute_${attributeId}`;
}

export function getOrganizationAssignmentAttributeValues(
  attributes: OrganizationAssignmentAttributeValue[],
) {
  const attributeValues: Record<string, string | null> = {};

  for (const attribute of attributes) {
    attributeValues[
      getOrganizationAssignmentAttributeColumnId(attribute.badge_attribute_id)
    ] = attribute.formatted_value ?? attribute.value ?? null;
  }

  return attributeValues;
}

export function getOrganizationAssignmentWhere({
  badgeIds,
  organizationId,
  personId,
  searchTarget,
  searchTerm,
  teamIds,
}: {
  badgeIds: string[];
  organizationId?: string | null;
  personId?: string | null;
  searchTarget: OrganizationAssignmentSearchTarget;
  searchTerm: string;
  teamIds: string[];
}): BadgeAssigneeBoolExp {
  if (!organizationId && !teamIds.length) {
    return {
      id: { _eq: NO_ORGANIZATION_ASSIGNMENT_ID },
    };
  }

  const filters: BadgeAssigneeBoolExp[] = [];

  if (teamIds.length) {
    filters.push({
      team_id: { _in: teamIds },
    });
  } else if (organizationId) {
    filters.push({
      event_id: { _eq: organizationId },
    });
  }

  if (personId) {
    filters.push({
      person_id: { _eq: personId },
    });
  }

  if (badgeIds.length) {
    filters.push({
      badge_id: { _in: badgeIds },
    });
  }

  if (searchTerm.length >= ORGANIZATION_ASSIGNMENT_SEARCH_MINIMUM) {
    filters.push(
      getOrganizationAssignmentSearchFilter(searchTarget, searchTerm),
    );
  }

  return {
    _and: filters,
  };
}

export function toOrganizationAssignmentRow(
  assignment: OrganizationAssignment,
): OrganizationAssignmentRow {
  return {
    id: assignment.id,
    assignmentId: assignment.id,
    badgeId: assignment.badge.id,
    badgeTitle: assignment.badge.title ?? 'Untitled Badge',
    badgeColor: assignment.badge.color ?? null,
    personId: assignment.person?.id ?? assignment.person_id ?? '',
    personName: assignment.person?.name ?? 'Unknown Member',
    personAvatarId: assignment.person?.avatar_id ?? null,
    personColor: assignment.person?.color ?? null,
    context: getOrganizationAssignmentContext(assignment),
    attributes: formatOrganizationAssignmentAttributes(
      assignment.badge_assignee_attributes,
    ),
    createdAt: assignment.created_at ?? null,
    updatedAt: assignment.updated_at ?? null,
    eventId: assignment.event_id ?? null,
    teamId: assignment.team_id ?? null,
    ...getOrganizationAssignmentAttributeValues(
      assignment.badge_assignee_attributes,
    ),
  };
}

function getOrganizationAssignmentSearchFilter(
  searchTarget: OrganizationAssignmentSearchTarget,
  searchTerm: string,
): BadgeAssigneeBoolExp {
  if (searchTarget === 'person') {
    return {
      person: { name: { _ilike: `%${searchTerm}%` } },
    };
  }

  return {
    badge: { title: { _ilike: `%${searchTerm}%` } },
  };
}

function getOrganizationAssignmentContext(assignment: OrganizationAssignment) {
  return assignment.team?.title ?? assignment.event?.title ?? 'Organization';
}

function formatOrganizationAssignmentAttributes(
  attributes: OrganizationAssignmentAttributeValue[],
) {
  const labels = attributes
    .map((attribute) => {
      const value = attribute.formatted_value ?? attribute.value;

      if (!value) {
        return null;
      }

      return `${attribute.badge_attribute.title ?? 'Attribute'}: ${value}`;
    })
    .filter((label) => Boolean(label));

  return labels.length ? labels.join(', ') : '-';
}

function getOrganizationAssignmentSelectionColumns(
  options: OrganizationAssignmentSelectionColumnsOptions,
): TableNextColumn<OrganizationAssignmentRow>[] {
  const selectionColumn = hasStoreSelection(options)
    ? getStoreSelectionColumn(options)
    : getLegacySelectionColumn(options);

  return [selectionColumn];
}

function hasStoreSelection(
  options: OrganizationAssignmentSelectionColumnsOptions,
): options is OrganizationAssignmentStoreSelectionOptions & {
  assignmentCount: number;
  testIDPrefix: string;
} {
  return Boolean(options.selectionStore);
}

function getStoreSelectionColumn({
  assignmentCount,
  selectionStore,
  testIDPrefix,
  visibleAssignmentIds,
}: OrganizationAssignmentStoreSelectionOptions & {
  assignmentCount: number;
  testIDPrefix: string;
}): TableNextColumn<OrganizationAssignmentRow> {
  return {
    title: '',
    id: 'selected',
    width: SELECTION_COLUMN_WIDTH,
    cellStyle: styles.selectionCell,
    headerCellStyle: styles.selectionCell,
    renderHeaderCell: () => (
      <TableSelectionHeaderCheckbox
        disabled={!assignmentCount}
        selectionStore={selectionStore}
        style={styles.selectionHeaderCheckbox}
        testID={`${testIDPrefix}SelectAssignmentsCheckbox`}
        visibleIds={visibleAssignmentIds}
      />
    ),
    renderCell: (rowData) => (
      <TableSelectionCheckbox
        selectionStore={selectionStore}
        style={styles.selectionCheckbox}
        testID={`${testIDPrefix}SelectAssignment-${rowData.assignmentId}`}
        value={rowData.assignmentId}
      />
    ),
  };
}

function getLegacySelectionColumn({
  allVisibleAssignmentsSelected,
  assignmentCount,
  onValueChangeAllVisibleAssignmentsSelected,
  selectedAssignmentIds,
  testIDPrefix,
  toggleSelectedAssignment,
}: OrganizationAssignmentLegacySelectionOptions & {
  assignmentCount: number;
  testIDPrefix: string;
}): TableNextColumn<OrganizationAssignmentRow> {
  return {
    title: '',
    id: 'selected',
    width: SELECTION_COLUMN_WIDTH,
    cellStyle: styles.selectionCell,
    headerCellStyle: styles.selectionCell,
    renderHeaderCell: () => (
      <Checkbox
        disabled={!assignmentCount}
        size="sm"
        value={allVisibleAssignmentsSelected}
        style={styles.selectionHeaderCheckbox}
        testID={`${testIDPrefix}SelectAssignmentsCheckbox`}
        onValueChange={onValueChangeAllVisibleAssignmentsSelected}
      />
    ),
    renderCell: (rowData) => {
      const selected = selectedAssignmentIds.includes(rowData.assignmentId);

      return (
        <Checkbox
          size="sm"
          value={selected}
          style={styles.selectionCheckbox}
          testID={`${testIDPrefix}SelectAssignment-${rowData.assignmentId}`}
          onValueChange={(nextSelected) => {
            toggleSelectedAssignment(rowData.assignmentId, nextSelected);
          }}
        />
      );
    },
  };
}

function getOrganizationAssignmentColumns(
  options: OrganizationAssignmentColumnsOptions & {
    primaryColumn: TableNextColumn<OrganizationAssignmentRow>;
  },
): TableNextColumn<OrganizationAssignmentRow>[] {
  const { canSelectAssignments, primaryColumn, selectedBadgeAttributes } =
    options;
  const selectionColumns: TableNextColumn<OrganizationAssignmentRow>[] =
    canSelectAssignments
      ? getOrganizationAssignmentSelectionColumns(options)
      : [];
  const attributeColumns = selectedBadgeAttributes.length
    ? selectedBadgeAttributes.map(
        (attribute): TableNextColumn<OrganizationAssignmentRow> => ({
          title: attribute.title ?? 'Attribute',
          id: getOrganizationAssignmentAttributeColumnId(attribute.id),
          maxWidth: ATTRIBUTE_COLUMN_MAX_WIDTH,
          flexGrow: 0,
          renderCell: (rowData) => {
            const attributeValue =
              rowData[getOrganizationAssignmentAttributeColumnId(attribute.id)];
            const displayValue =
              typeof attributeValue === 'number' ||
              typeof attributeValue === 'string'
                ? attributeValue
                : '-';

            return (
              <Text size="sm" numberOfLines={1}>
                {displayValue || '-'}
              </Text>
            );
          },
        }),
      )
    : [getConsolidatedAttributeColumn()];

  return [
    ...selectionColumns,
    primaryColumn,
    {
      title: 'Context',
      id: 'context',
      maxWidth: DYNAMIC_COLUMN_MAX_WIDTH,
      flexGrow: 0,
      renderCell: (rowData) => (
        <Text size="sm" numberOfLines={1}>
          {rowData.context}
        </Text>
      ),
    },
    ...attributeColumns,
    {
      title: 'Created',
      id: 'createdAt',
      width: DATE_COLUMN_WIDTH,
      getAutosizeValue: (rowData) =>
        formatAssignmentTableDate(rowData.createdAt),
      renderCell: (rowData) => (
        <Text size="sm" numberOfLines={1}>
          {formatAssignmentTableDate(rowData.createdAt)}
        </Text>
      ),
    },
    {
      title: 'Updated',
      id: 'updatedAt',
      width: DATE_COLUMN_WIDTH,
      getAutosizeValue: (rowData) =>
        formatAssignmentTableDate(rowData.updatedAt),
      renderCell: (rowData) => (
        <Text size="sm" numberOfLines={1}>
          {formatAssignmentTableDate(rowData.updatedAt)}
        </Text>
      ),
    },
  ];
}

function getBadgeColumn(): TableNextColumn<OrganizationAssignmentRow> {
  return {
    title: 'Badge',
    id: 'badgeTitle',
    maxWidth: DYNAMIC_COLUMN_MAX_WIDTH,
    flexGrow: 0,
    autosizeExtraWidth: TABLE_NEXT_LEADING_VISUAL_AUTOSIZE_EXTRA_WIDTH,
    renderCell: (rowData) => (
      <Box
        direction="horizontal"
        align="center"
        flexGrow={1}
        flexShrink={1}
        minWidth={0}
        space="sm"
        px="sm"
      >
        <BadgeMask size="sm" color={rowData.badgeColor ?? undefined}>
          <Avatar
            ring={false}
            size="sm"
            color={rowData.badgeColor ?? ''}
            name={rowData.badgeTitle}
          />
        </BadgeMask>
        <Text size="sm" numberOfLines={1} flexShrink={1}>
          {rowData.badgeTitle}
        </Text>
      </Box>
    ),
  };
}

function getPersonColumn(): TableNextColumn<OrganizationAssignmentRow> {
  return {
    title: 'Person',
    id: 'personName',
    maxWidth: DYNAMIC_COLUMN_MAX_WIDTH,
    flexGrow: 0,
    autosizeExtraWidth: TABLE_NEXT_LEADING_VISUAL_AUTOSIZE_EXTRA_WIDTH,
    renderCell: (rowData) => (
      <Box
        direction="horizontal"
        align="center"
        flexShrink={1}
        minWidth={0}
        space="sm"
        px="sm"
      >
        <Avatar
          id={rowData.personAvatarId ?? ''}
          size="sm"
          color={rowData.personColor ?? ''}
          name={rowData.personName}
        />
        <Text
          size="sm"
          numberOfLines={1}
          flexShrink={1}
          style={styles.personNameText}
        >
          {rowData.personName}
        </Text>
      </Box>
    ),
  };
}

function getConsolidatedAttributeColumn(): TableNextColumn<OrganizationAssignmentRow> {
  return {
    title: 'Attributes',
    id: 'attributes',
    maxWidth: DYNAMIC_COLUMN_MAX_WIDTH,
    flexGrow: 0,
    renderCell: (rowData) => (
      <Text size="sm" numberOfLines={1}>
        {rowData.attributes}
      </Text>
    ),
  };
}

function formatAssignmentTableDate(value: string | null) {
  if (!value) {
    return '-';
  }

  const dateTime = DateTime.fromISO(value);

  return dateTime.isValid
    ? dateTime.toFormat(ASSIGNMENT_TABLE_DATE_TIME_FORMAT)
    : '-';
}

const styles = StyleSheet.create((theme) => ({
  selectionCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  personNameText: {
    minWidth: 0,
  },
  selectionCheckbox: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    justifyContent: 'center',
    minHeight: theme.size(12),
    width: '100%',
  },
  selectionHeaderCheckbox: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    justifyContent: 'center',
    minHeight: theme.size(10),
    width: '100%',
  },
}));
